import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Delete,
  Edit,
  EventBusy,
  Today,
  Cancel,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import HousePageBackLink from '../components/HousePageBackLink';
import axios from '../api/axios';
import { formatDate, formatDateRange, formatPrice } from '../utils/format';

const WEEKDAY_REFERENCE = new Date(2024, 0, 7);

const toDayKey = (value: string | Date) => {
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }

  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const addDays = (value: Date, amount: number) => {
  const next = new Date(value);
  next.setDate(next.getDate() + amount);
  return next;
};

const getMonthStart = (value: Date) => new Date(value.getFullYear(), value.getMonth(), 1);

const buildCalendarDays = (month: Date) => {
  const startOfMonth = getMonthStart(month);
  const gridStart = addDays(startOfMonth, -startOfMonth.getDay());

  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

const isDayWithinRange = (dayKey: string, startDate: string, endDate: string) => {
  const normalizedStart = startDate.slice(0, 10);
  const normalizedEnd = endDate.slice(0, 10);
  return dayKey >= normalizedStart && dayKey <= normalizedEnd;
};

type BookingStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
type DayStatus = 'free' | 'busy' | 'pending' | 'blocked';

interface Booking {
  id: string;
  startDate: string;
  endDate: string;
  guest?: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  numberOfGuests: number;
  status: BookingStatus;
  message?: string;
  hostMessage?: string;
  createdAt: string;
  updatedAt?: string;
  isOwnBooking?: boolean;
}

interface UnavailablePeriod {
  id: string;
  startDate: string;
  endDate: string;
  label?: string;
  isAvailable: boolean;
  createdAt: string;
}

interface AvailabilityRule {
  maxConsecutiveDays?: number;
  maxBookingLeadDays?: number;
  maxOccupants?: number;
  contributionCost?: number;
  contributionDescription?: string;
}

interface CalendarOverview {
  bookings: Booking[];
  unavailablePeriods: UnavailablePeriod[];
}

interface CalendarPageProps {
  embedded?: boolean;
  isOwner?: boolean;
}

export const CalendarPage: React.FC<CalendarPageProps> = ({ embedded = false, isOwner = false }) => {
  const { id: houseId } = useParams<{ id: string }>();
  const todayKey = toDayKey(new Date());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [unavailablePeriods, setUnavailablePeriods] = useState<UnavailablePeriod[]>([]);
  const [rules, setRules] = useState<AvailabilityRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(getMonthStart(new Date()));
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [unavailableDialogOpen, setUnavailableDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [bookingDetailDay, setBookingDetailDay] = useState<string | null>(null);
  const [bookingConflictMessage, setBookingConflictMessage] = useState<string | null>(null);
  const [respondBookingId, setRespondBookingId] = useState<string | null>(null);
  const [respondAction, setRespondAction] = useState<'approve' | 'reject' | null>(null);
  const [respondMessage, setRespondMessage] = useState('');
  const [selectionStart, setSelectionStart] = useState<string | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<string | null>(null);
  const [bookingFormData, setBookingFormData] = useState({
    startDate: '',
    endDate: '',
    numberOfGuests: '1',
    message: '',
  });
  const [rulesFormData, setRulesFormData] = useState({
    maxConsecutiveDays: '',
    maxBookingLeadDays: '',
    maxOccupants: '',
    contributionCost: '',
    contributionDescription: '',
  });
  const [unavailableFormData, setUnavailableFormData] = useState({
    startDate: todayKey,
    endDate: todayKey,
    label: '',
  });
  const { t, i18n } = useTranslation();

  const refreshCalendar = useCallback(async () => {
    if (!houseId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [overviewResponse, houseResponse] = await Promise.all([
        axios.get<CalendarOverview>(`/calendar/overview?houseId=${houseId}`),
        axios.get(`/houses/${houseId}`),
      ]);

      setBookings(overviewResponse.data.bookings);
      setUnavailablePeriods(overviewResponse.data.unavailablePeriods);
      setRules(houseResponse.data);
      setRulesFormData({
        maxConsecutiveDays: houseResponse.data.maxConsecutiveDays?.toString() || '',
        maxBookingLeadDays: houseResponse.data.maxBookingLeadDays?.toString() || '',
        maxOccupants: houseResponse.data.maxOccupants?.toString() || '',
        contributionCost: houseResponse.data.contributionCost?.toString() || '',
        contributionDescription: houseResponse.data.contributionDescription || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [houseId, t]);

  useEffect(() => {
    refreshCalendar();
  }, [refreshCalendar]);

  const monthDays = buildCalendarDays(currentMonth);
  const weekdayLabels = Array.from({ length: 7 }, (_, index) => {
    return new Intl.DateTimeFormat(i18n.resolvedLanguage, { weekday: isMobile ? 'narrow' : 'short' }).format(addDays(WEEKDAY_REFERENCE, index));
  });
  const sortedBookings = [...bookings].sort((left, right) => left.startDate.localeCompare(right.startDate));
  const sortedUnavailablePeriods = [...unavailablePeriods].sort((left, right) => left.startDate.localeCompare(right.startDate));
  const bookingsByPriority = [...sortedBookings].sort((left, right) => {
    const priority: Record<BookingStatus, number> = {
      pending: 0,
      approved: 1,
      rejected: 2,
      cancelled: 3,
    };

    const statusDifference = priority[left.status] - priority[right.status];
    if (statusDifference !== 0) {
      return statusDifference;
    }

    return left.startDate.localeCompare(right.startDate);
  });
  const displayBookings = bookingsByPriority;
  const hasSelection = selectionStart !== null;
  const selectionComplete = selectionStart !== null && selectionEnd !== null;

  const getBookingsForDay = (dayKey: string) => {
    return bookings.filter(booking => isDayWithinRange(dayKey, booking.startDate, booking.endDate));
  };

  const getUnavailablePeriodsForDay = (dayKey: string) => {
    return unavailablePeriods.filter(period => isDayWithinRange(dayKey, period.startDate, period.endDate));
  };

  const getDayStatus = (dayKey: string): DayStatus => {
    const dayUnavailablePeriods = getUnavailablePeriodsForDay(dayKey);
    if (dayUnavailablePeriods.length > 0) {
      return 'blocked';
    }

    const dayBookings = getBookingsForDay(dayKey);
    if (dayBookings.some(booking => booking.status === 'approved')) {
      return 'busy';
    }

    if (dayBookings.some(booking => booking.status === 'pending')) {
      return 'pending';
    }

    return 'free';
  };

  const isDaySelectable = (dayKey: string) => {
    if (isOwner) return false;
    if (dayKey < todayKey) return false;

    const status = getDayStatus(dayKey);
    if (status === 'blocked' || status === 'busy') return false;

    if (rules?.maxBookingLeadDays) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(dayKey + 'T00:00:00');
      const leadDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (leadDays > rules.maxBookingLeadDays) return false;
    }

    if (selectionStart && !selectionEnd) {
      if (dayKey < selectionStart) return false;

      if (rules?.maxConsecutiveDays) {
        const start = new Date(selectionStart + 'T00:00:00');
        const target = new Date(dayKey + 'T00:00:00');
        const dayCount = Math.ceil((target.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        if (dayCount > rules.maxConsecutiveDays) return false;
      }

      // Check no blocked/busy day between start and this day
      const start = new Date(selectionStart + 'T00:00:00');
      const target = new Date(dayKey + 'T00:00:00');
      const cursor = new Date(start);
      cursor.setDate(cursor.getDate() + 1);
      while (cursor <= target) {
        const cursorKey = toDayKey(cursor);
        const cursorStatus = getDayStatus(cursorKey);
        if (cursorStatus === 'blocked' || cursorStatus === 'busy') return false;
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return true;
  };

  const isDayInSelection = (dayKey: string) => {
    if (!selectionStart) return false;
    if (!selectionEnd) return dayKey === selectionStart;
    return dayKey >= selectionStart && dayKey <= selectionEnd;
  };

  const handleHostDayClick = (dayKey: string) => {
    const dayBookings = getBookingsForDay(dayKey);
    const activeBookings = dayBookings.filter(b => b.status === 'approved' || b.status === 'pending');
    if (activeBookings.length > 0) {
      setBookingDetailDay(dayKey);
    }
  };

  const handleDayClick = (dayKey: string) => {
    if (!isDaySelectable(dayKey)) return;

    if (!selectionStart || selectionComplete) {
      // Start new selection
      setSelectionStart(dayKey);
      setSelectionEnd(null);
      setSelectedBooking(null);
      setBookingConflictMessage(null);
      setBookingFormData({
        startDate: dayKey,
        endDate: dayKey,
        numberOfGuests: '1',
        message: '',
      });
    } else {
      // Complete selection
      setSelectionEnd(dayKey);
      setBookingFormData(prev => ({
        ...prev,
        startDate: selectionStart,
        endDate: dayKey,
      }));
    }
  };

  const clearSelection = () => {
    setSelectionStart(null);
    setSelectionEnd(null);
    setBookingConflictMessage(null);
  };

  const getDayStatusLabel = (status: DayStatus) => {
    switch (status) {
      case 'busy':
        return t('calendar.legendBusy');
      case 'pending':
        return t('calendar.legendPending');
      case 'blocked':
        return t('calendar.legendBlocked');
      default:
        return t('calendar.legendFree');
    }
  };

  const getDayStatusColor = (status: DayStatus) => {
    switch (status) {
      case 'busy':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'blocked':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[400];
    }
  };

  const getDayStatusStyles = (status: DayStatus) => {
    const statusColor = getDayStatusColor(status);
    const base = {
      borderColor: alpha(statusColor, 0.2),
      backgroundColor: alpha(statusColor, 0.1),
      color: 'text.primary',
      boxShadow: 'none',
    } as const;

    if (status === 'free') {
      return {
        ...base,
        borderColor: alpha(theme.palette.divider, 0.9),
        backgroundColor: alpha(theme.palette.common.white, 0.9),
      };
    }

    return base;
  };

  const getStatusChip = (status: BookingStatus) => {
    switch (status) {
      case 'approved':
        return <Chip label={t('common.statuses.approved')} color="success" size="small" />;
      case 'pending':
        return <Chip label={t('common.statuses.pending')} color="warning" size="small" />;
      case 'rejected':
        return <Chip label={t('common.statuses.rejected')} color="error" size="small" />;
      case 'cancelled':
        return <Chip label={t('common.statuses.cancelled')} color="default" size="small" />;
      default:
        return null;
    }
  };

  const getGuestLabel = (booking: Booking) => {
    if (booking.guest) {
      return `${booking.guest.firstName} ${booking.guest.lastName}`.trim();
    }

    if (booking.isOwnBooking) {
      return t('calendar.yourBooking');
    }

    if (booking.status === 'approved') {
      return t('calendar.houseBusy');
    }

    return t('calendar.bookingRequest');
  };

  const getBookingErrorMessage = (error: any) => {
    const backendMessage = error?.response?.data?.message;
    const normalizedMessage = Array.isArray(backendMessage) ? backendMessage.join(' ') : backendMessage;

    switch (normalizedMessage) {
      case 'This booking overlaps a period when the house is unavailable':
        return t('calendar.conflictUnavailable');
      case 'This booking falls outside the available booking periods for the house':
        return t('calendar.conflictOutsideAvailability');
      case 'This booking overlaps an approved stay':
        return t('calendar.conflictApprovedStay');
      case 'This booking is too far in the future':
        return t('calendar.conflictTooFarAhead');
      default:
        return normalizedMessage || t('calendar.failedSave');
    }
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setBookingConflictMessage(null);
    setSelectionStart(booking.startDate.split('T')[0]);
    setSelectionEnd(booking.endDate.split('T')[0]);
    setBookingFormData({
      startDate: booking.startDate.split('T')[0],
      endDate: booking.endDate.split('T')[0],
      numberOfGuests: booking.numberOfGuests.toString(),
      message: booking.message || '',
    });
  };

  const handleDeleteBooking = async (bookingId: string) => {
    if (!window.confirm(t('calendar.deleteConfirm'))) {
      return;
    }

    try {
      await axios.delete(`/calendar/bookings/${bookingId}`);
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedDelete'));
    }
  };

  const handleApproveBooking = async (bookingId: string, message?: string) => {
    try {
      await axios.patch(`/calendar/bookings/${bookingId}/approve`, { message });
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedApprove'));
    }
  };

  const handleRejectBooking = async (bookingId: string, message?: string) => {
    try {
      await axios.patch(`/calendar/bookings/${bookingId}/reject`, { message });
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedReject'));
    }
  };

  const openRespondDialog = (bookingId: string, action: 'approve' | 'reject') => {
    setRespondBookingId(bookingId);
    setRespondAction(action);
    setRespondMessage('');
  };

  const closeRespondDialog = () => {
    setRespondBookingId(null);
    setRespondAction(null);
    setRespondMessage('');
  };

  const handleConfirmRespond = async () => {
    if (!respondBookingId || !respondAction) return;
    const message = respondMessage.trim() || undefined;
    if (respondAction === 'approve') {
      await handleApproveBooking(respondBookingId, message);
    } else {
      await handleRejectBooking(respondBookingId, message);
    }
    closeRespondDialog();
  };

  const handleSubmitBooking = async () => {
    try {
      setBookingConflictMessage(null);
      const payload = {
        startDate: bookingFormData.startDate,
        endDate: bookingFormData.endDate,
        numberOfGuests: parseInt(bookingFormData.numberOfGuests, 10),
        message: bookingFormData.message || undefined,
        houseId,
      };

      if (selectedBooking) {
        await axios.put(`/calendar/bookings/${selectedBooking.id}`, payload);
      } else {
        await axios.post('/calendar/bookings', payload);
      }

      clearSelection();
      setBookingConflictMessage(null);
      await refreshCalendar();
    } catch (err: any) {
      const message = getBookingErrorMessage(err);
      setBookingConflictMessage(message);
      setError(message);
    }
  };

  const handleSubmitRules = async () => {
    try {
      const payload = {
        maxConsecutiveDays: rulesFormData.maxConsecutiveDays ? parseInt(rulesFormData.maxConsecutiveDays, 10) : undefined,
        maxBookingLeadDays: rulesFormData.maxBookingLeadDays ? parseInt(rulesFormData.maxBookingLeadDays, 10) : undefined,
        maxOccupants: rulesFormData.maxOccupants ? parseInt(rulesFormData.maxOccupants, 10) : undefined,
        contributionCost: rulesFormData.contributionCost ? parseFloat(rulesFormData.contributionCost) : undefined,
        contributionDescription: rulesFormData.contributionDescription || undefined,
      };

      await axios.put(`/houses/${houseId}`, payload);
      setRulesDialogOpen(false);
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedSaveRules'));
    }
  };

  const openCreateUnavailablePeriod = () => {
    setUnavailableFormData({
      startDate: todayKey,
      endDate: todayKey,
      label: '',
    });
    setUnavailableDialogOpen(true);
  };

  const handleSubmitUnavailablePeriod = async () => {
    try {
      await axios.post('/calendar/availability', {
        houseId,
        startDate: unavailableFormData.startDate,
        endDate: unavailableFormData.endDate,
        label: unavailableFormData.label || undefined,
        isAvailable: false,
      });
      setUnavailableDialogOpen(false);
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedSaveAvailability'));
    }
  };

  const handleDeleteUnavailablePeriod = async (availabilityId: string) => {
    if (!window.confirm(t('calendar.deleteUnavailableConfirm'))) {
      return;
    }

    try {
      await axios.delete(`/calendar/availability/${availabilityId}`);
      await refreshCalendar();
    } catch (err: any) {
      setError(err.response?.data?.message || t('calendar.failedDeleteAvailability'));
    }
  };

  const content = (
    <Container maxWidth={embedded ? false : 'lg'} sx={{ px: embedded ? 0 : undefined }}>
      {!embedded && <HousePageBackLink />}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="h4">{t('calendar.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
              {t('calendar.description')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {isOwner && (
            <>
              <Button variant="outlined" startIcon={<EventBusy />} onClick={openCreateUnavailablePeriod}>
                {t('calendar.blockPeriod')}
              </Button>
              <Button variant="outlined" onClick={() => setRulesDialogOpen(true)}>
                {t('calendar.manageRules')}
              </Button>
            </>
          )}
          </Box>
        </Stack>
      </Box>

      {rules && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography variant={isMobile ? 'subtitle2' : 'h6'} gutterBottom>
              {t('common.labels.availabilityRules')}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {rules.maxConsecutiveDays && (
                <Grid item xs={6} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.labels.maxConsecutiveDays')}
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'}>{rules.maxConsecutiveDays}</Typography>
                </Grid>
              )}
              {rules.maxBookingLeadDays && (
                <Grid item xs={6} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.labels.maxBookingLeadDays')}
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'}>{rules.maxBookingLeadDays}</Typography>
                </Grid>
              )}
              {rules.maxOccupants && (
                <Grid item xs={6} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.labels.maxGuests')}
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'}>{rules.maxOccupants}</Typography>
                </Grid>
              )}
              {rules.contributionCost && (
                <Grid item xs={6} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.labels.contribution')}
                  </Typography>
                  <Typography variant={isMobile ? 'body1' : 'h6'}>{formatPrice(rules.contributionCost, i18n.resolvedLanguage)}</Typography>
                </Grid>
              )}
              {rules.contributionDescription && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {rules.contributionDescription}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start', mb: 3 }}>
      <Box sx={{ flex: { xs: '1 1 0', md: '0 1 auto' }, minWidth: 0, width: { md: '55%' } }}>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 }, '&:last-child': { pb: { xs: 1.5, sm: 3 } } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, alignItems: 'center', mb: { xs: 1.5, sm: 3 } }}>
            <Box>
              <Typography variant="h6">{t('calendar.monthOverview')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('calendar.monthOverviewHelper')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-end' }, gap: { xs: 0.5, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
              <IconButton size={isMobile ? 'small' : 'medium'} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                <ChevronLeft />
              </IconButton>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ flex: 1, minWidth: { xs: 0, sm: 180 }, textAlign: 'center', textTransform: 'capitalize' }}>
                {new Intl.DateTimeFormat(i18n.resolvedLanguage, { month: 'long', year: 'numeric' }).format(currentMonth)}
              </Typography>
              <IconButton size={isMobile ? 'small' : 'medium'} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                <ChevronRight />
              </IconButton>
              <Button size={isMobile ? 'small' : 'medium'} variant="text" startIcon={!isMobile ? <Today /> : undefined} onClick={() => {
                setCurrentMonth(getMonthStart(new Date()));
              }}>
                {t('calendar.today')}
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.25, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
            {(['free', 'busy', 'pending', 'blocked'] as DayStatus[]).map(status => (
              <Box
                key={status}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: getDayStatusColor(status),
                    boxShadow: `0 0 0 4px ${alpha(getDayStatusColor(status), 0.14)}`,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {getDayStatusLabel(status)}
                </Typography>
              </Box>
            ))}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 1 }, mb: { xs: 0.5, sm: 1 } }}>
                  {weekdayLabels.map((label, index) => (
                    <Box key={`${label}-${index}`} sx={{ px: { xs: 0.25, sm: 0.75 }, py: 0.25, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: { xs: 0.2, sm: 0.6 }, fontSize: { xs: '0.62rem', sm: '0.75rem' } }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 1 } }}>
                  {monthDays.map(day => {
                    const dayKey = toDayKey(day);
                    const dayBookings = getBookingsForDay(dayKey);
                    const dayUnavailable = getUnavailablePeriodsForDay(dayKey);
                    const dayStatus = getDayStatus(dayKey);
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const statusStyles = getDayStatusStyles(dayStatus);
                    const summaryCount = dayUnavailable.length + dayBookings.filter(booking => booking.status === 'approved' || booking.status === 'pending').length;
                    const selectable = isDaySelectable(dayKey);
                    const inSelection = isDayInSelection(dayKey);
                    const isSelectionEdge = dayKey === selectionStart || dayKey === selectionEnd;
                    const isHostClickable = isOwner && (dayStatus === 'busy' || dayStatus === 'pending');
                    const isClickable = selectable || isHostClickable;

                    return (
                      <Box
                        key={dayKey}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : -1}
                        onClick={selectable ? () => handleDayClick(dayKey) : isHostClickable ? () => handleHostDayClick(dayKey) : undefined}
                        onKeyDown={isClickable ? (event: React.KeyboardEvent) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            if (selectable) handleDayClick(dayKey);
                            else if (isHostClickable) handleHostDayClick(dayKey);
                          }
                        } : undefined}
                        aria-label={`${formatDate(day, i18n.resolvedLanguage)} - ${getDayStatusLabel(dayStatus)}`}
                        sx={{
                          aspectRatio: { xs: '1 / 1', sm: 'auto' },
                          minHeight: { xs: 42, sm: 84, md: 56 },
                          border: '2px solid',
                          borderRadius: { xs: 1.25, sm: 1.5 },
                          px: { xs: 0.5, sm: 0.75 },
                          py: { xs: 0.35, sm: 0.65 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          cursor: isClickable ? 'pointer' : 'default',
                          opacity: (!isOwner && !selectable) ? 0.45 : 1,
                          transition: 'border-color 120ms, background-color 120ms, box-shadow 120ms',
                          ...(inSelection ? {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, isSelectionEdge ? 0.18 : 0.08),
                            boxShadow: isSelectionEdge ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          } : {
                            ...statusStyles,
                            ...(!isCurrentMonth && { backgroundColor: alpha(theme.palette.grey[200], 0.5) }),
                          }),
                          ...(selectable && !inSelection && {
                            '&:hover': {
                              borderColor: alpha(theme.palette.primary.main, 0.5),
                              backgroundColor: alpha(theme.palette.primary.main, 0.06),
                            },
                          }),
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.25 }}>
                          <Typography variant={isMobile ? 'caption' : 'subtitle2'} sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                            {day.getDate()}
                          </Typography>
                          <Box
                            sx={{
                              width: { xs: 7, sm: 8 },
                              height: { xs: 7, sm: 8 },
                              borderRadius: '50%',
                              bgcolor: getDayStatusColor(dayStatus),
                              flexShrink: 0,
                              mt: 0.2,
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minHeight: { xs: 10, sm: 16 } }}>
                          <Box sx={{ display: 'flex', gap: 0.35, alignItems: 'center' }}>
                            {dayUnavailable.length > 0 && (
                              <Box sx={{ width: { xs: 10, sm: 14 }, height: 3, borderRadius: 999, bgcolor: alpha(theme.palette.error.main, 0.9) }} />
                            )}
                            {dayBookings.some(booking => booking.status === 'approved') && (
                              <Box sx={{ width: { xs: 10, sm: 14 }, height: 3, borderRadius: 999, bgcolor: alpha(theme.palette.success.main, 0.9) }} />
                            )}
                            {dayBookings.some(booking => booking.status === 'pending') && (
                              <Box sx={{ width: { xs: 10, sm: 14 }, height: 3, borderRadius: 999, bgcolor: alpha(theme.palette.warning.main, 0.9) }} />
                            )}
                          </Box>
                          {summaryCount > 0 && (
                            <Box
                              sx={{
                                minWidth: { xs: 14, sm: 18 },
                                height: { xs: 14, sm: 18 },
                                px: summaryCount > 9 ? 0.4 : 0,
                                borderRadius: 999,
                                bgcolor: alpha(theme.palette.text.primary, 0.1),
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: { xs: 9, sm: 11 },
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {summaryCount}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}

          {!isOwner && !loading && (
            <Box sx={{ mt: 2, px: 0.5 }}>
              {!hasSelection && (
                <Typography variant="body2" color="text.secondary">
                  {t('calendar.selectionHintStart')}
                </Typography>
              )}
              {hasSelection && !selectionComplete && (
                <Typography variant="body2" color="primary">
                  {t('calendar.selectionHintEnd')}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      </Box>

      {!isOwner && (isDesktop || selectionComplete) && (
        <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
        <Card sx={{ border: '2px solid', borderColor: selectionComplete ? 'primary.main' : 'divider' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {selectedBooking ? t('calendar.editBooking') : t('calendar.newBooking')}
              </Typography>
              {selectionComplete && <Button size="small" onClick={clearSelection}>{t('common.actions.cancel')}</Button>}
            </Box>
            {!selectionComplete && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('calendar.selectionHintStart')}
              </Typography>
            )}
            {bookingConflictMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bookingConflictMessage}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.fields.startDate')}
                  value={bookingFormData.startDate}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.fields.endDate')}
                  value={bookingFormData.endDate}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label={t('common.labels.numberOfGuests')}
              value={bookingFormData.numberOfGuests}
              onChange={(event) => setBookingFormData({ ...bookingFormData, numberOfGuests: event.target.value })}
              type="number"
              inputProps={{ min: 1, ...(rules?.maxOccupants ? { max: rules.maxOccupants } : {}) }}
              sx={{ mb: 2 }}
              required
              helperText={rules?.maxOccupants ? t('calendar.maxGuestsHelper') + ` (${rules.maxOccupants})` : undefined}
            />
            <TextField
              fullWidth
              label={t('common.fields.message')}
              value={bookingFormData.message}
              onChange={(event) => setBookingFormData({ ...bookingFormData, message: event.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" fullWidth onClick={handleSubmitBooking} disabled={!selectionComplete}>
              {selectedBooking ? t('common.actions.update') : t('calendar.request')}
            </Button>
          </CardContent>
        </Card>
        </Box>
      )}
      </Box>

      {!loading && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ mb: isOwner ? 3 : 0, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="h6">{t('calendar.bookingsPanelTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isOwner ? t('calendar.ownerBookingsHelper') : t('calendar.guestBookingsHelper')}
                    </Typography>
                  </Box>
                </Box>

                {displayBookings.length === 0 ? (
                  <Alert severity="info">
                    {isOwner ? t('calendar.noGuestRequests') : t('calendar.requestYourFirstStay')}
                  </Alert>
                ) : (
                  <List>
                    {displayBookings.map((booking, index) => (
                      <React.Fragment key={booking.id}>
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isOwner && booking.status === 'pending' && (
                                <>
                                  <IconButton size="small" color="success" onClick={() => openRespondDialog(booking.id, 'approve')}>
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => openRespondDialog(booking.id, 'reject')}>
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {!isOwner && booking.isOwnBooking && (
                                <>
                                  <IconButton size="small" onClick={() => handleEditBooking(booking)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteBooking(booking.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 8 }}>
                                <Typography variant="subtitle1">{getGuestLabel(booking)}</Typography>
                                {getStatusChip(booking.status)}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>
                                  {formatDateRange(booking.startDate, booking.endDate, i18n.resolvedLanguage)}
                                </Typography>
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {t('common.labels.guestCount', { count: booking.numberOfGuests })}
                                </Typography>
                                {booking.message && (
                                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {booking.message}
                                  </Typography>
                                )}
                                {booking.hostMessage && (
                                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                    {t('calendar.hostResponse')}: {booking.hostMessage}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                        {index < displayBookings.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {isOwner && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{t('calendar.unavailablePeriodsTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('calendar.unavailablePeriodsHelper')}
                      </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<Add />} onClick={openCreateUnavailablePeriod}>
                      {t('calendar.blockPeriod')}
                    </Button>
                  </Box>

                  {sortedUnavailablePeriods.length === 0 ? (
                    <Alert severity="info">{t('calendar.noUnavailablePeriods')}</Alert>
                  ) : (
                    <List>
                      {sortedUnavailablePeriods.map((period, index) => (
                        <React.Fragment key={period.id}>
                          <ListItem
                            secondaryAction={
                              <IconButton edge="end" onClick={() => handleDeleteUnavailablePeriod(period.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={period.label || t('calendar.blockedLabelFallback')}
                              secondary={formatDateRange(period.startDate, period.endDate, i18n.resolvedLanguage)}
                            />
                            <Chip label={t('calendar.legendBlocked')} color="error" size="small" />
                          </ListItem>
                          {index < sortedUnavailablePeriods.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      <Dialog open={bookingDetailDay !== null} onClose={() => setBookingDetailDay(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('calendar.bookingDetailTitle')}</DialogTitle>
        <DialogContent>
          {bookingDetailDay && (() => {
            const dayBookings = getBookingsForDay(bookingDetailDay).filter(b => b.status === 'approved' || b.status === 'pending');
            return dayBookings.length === 0 ? (
              <Typography color="text.secondary">{t('calendar.noBookings')}</Typography>
            ) : (
              <List disablePadding>
                {dayBookings.map((booking, index) => (
                  <React.Fragment key={booking.id}>
                    <ListItem alignItems="flex-start" disableGutters
                      secondaryAction={
                        booking.status === 'pending' ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" color="success" onClick={() => { openRespondDialog(booking.id, 'approve'); setBookingDetailDay(null); }}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => { openRespondDialog(booking.id, 'reject'); setBookingDetailDay(null); }}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : undefined
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: booking.status === 'pending' ? 8 : 0 }}>
                            <Typography variant="subtitle1">{getGuestLabel(booking)}</Typography>
                            {getStatusChip(booking.status)}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>
                              {formatDateRange(booking.startDate, booking.endDate, i18n.resolvedLanguage)}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {t('common.labels.guestCount', { count: booking.numberOfGuests })}
                            </Typography>
                            {booking.guest?.email && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                {booking.guest.email}
                              </Typography>
                            )}
                            {booking.message && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                {booking.message}
                              </Typography>
                            )}
                            {booking.hostMessage && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                {t('calendar.hostResponse')}: {booking.hostMessage}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {index < dayBookings.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDetailDay(null)}>{t('common.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={respondBookingId !== null} onClose={closeRespondDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {respondAction === 'approve' ? t('calendar.approveBookingTitle') : t('calendar.rejectBookingTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('calendar.respondMessageLabel')}
            placeholder={t('calendar.respondMessagePlaceholder')}
            value={respondMessage}
            onChange={(e) => setRespondMessage(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRespondDialog}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            color={respondAction === 'approve' ? 'success' : 'error'}
            onClick={handleConfirmRespond}
          >
            {respondAction === 'approve' ? t('calendar.confirmApprove') : t('calendar.confirmReject')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unavailableDialogOpen} onClose={() => setUnavailableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('calendar.unavailableDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.fields.startDate')}
                  value={unavailableFormData.startDate}
                  onChange={(event) => setUnavailableFormData({ ...unavailableFormData, startDate: event.target.value })}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.fields.endDate')}
                  value={unavailableFormData.endDate}
                  onChange={(event) => setUnavailableFormData({ ...unavailableFormData, endDate: event.target.value })}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label={t('calendar.unavailableLabel')}
              value={unavailableFormData.label}
              onChange={(event) => setUnavailableFormData({ ...unavailableFormData, label: event.target.value })}
              helperText={t('calendar.unavailableLabelHelper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnavailableDialogOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleSubmitUnavailablePeriod} variant="contained">
            {t('calendar.saveUnavailablePeriod')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rulesDialogOpen} onClose={() => setRulesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('calendar.rulesTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t('common.labels.maxConsecutiveDays')}
              value={rulesFormData.maxConsecutiveDays}
              onChange={(event) => setRulesFormData({ ...rulesFormData, maxConsecutiveDays: event.target.value })}
              type="number"
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
              helperText={t('calendar.maxConsecutiveHelper')}
            />
            <TextField
              fullWidth
              label={t('common.labels.maxBookingLeadDays')}
              value={rulesFormData.maxBookingLeadDays}
              onChange={(event) => setRulesFormData({ ...rulesFormData, maxBookingLeadDays: event.target.value })}
              type="number"
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
              helperText={t('calendar.maxBookingLeadHelper')}
            />
            <TextField
              fullWidth
              label={t('common.labels.maxGuests')}
              value={rulesFormData.maxOccupants}
              onChange={(event) => setRulesFormData({ ...rulesFormData, maxOccupants: event.target.value })}
              type="number"
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
              helperText={t('calendar.maxGuestsHelper')}
            />
            <TextField
              fullWidth
              label={t('common.labels.contributionAmount')}
              value={rulesFormData.contributionCost}
              onChange={(event) => setRulesFormData({ ...rulesFormData, contributionCost: event.target.value })}
              type="number"
              inputProps={{ step: '0.01' }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('common.labels.contributionDescription')}
              value={rulesFormData.contributionDescription}
              onChange={(event) => setRulesFormData({ ...rulesFormData, contributionDescription: event.target.value })}
              multiline
              rows={3}
              helperText={t('common.helpers.contributionUsage')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRulesDialogOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleSubmitRules} variant="contained">
            {t('calendar.saveRules')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout title={t('calendar.layoutTitle')}>
      {content}
    </Layout>
  );
};

export default CalendarPage;
